import apiClient from '@/lib/api-client';
import type {
    Booking,
    BookingAvailabilityResponse,
    CreateBookingRequest,
    BookingSettings,
    BreakTime,
    PeakPricingRule,
    HolidayNotification,
    DismissedHoliday,
    Service,
    ServiceCategory,
    Customer,
    CustomerNote,
    CustomerActivity,
    BookingAnalytics,
    PortfolioAnalytics,
    PageAnalytics,
    FunnelAnalytics,
    FunnelStats,
    TimeSpentStats,
    LocationProfile,
} from '@/types';

// Generate a unique session ID for slot locking
const generateSessionId = (): string => {
    const array = new Uint8Array(16);
    crypto.getRandomValues(array);
    return Array.from(array, b => b.toString(16).padStart(2, '0')).join('') + Date.now().toString(16);
};

// Get or create booking session
const getBookingSessionId = (): string => {
    const sessionKey = 'booking_session_id';
    let sessionId = sessionStorage.getItem(sessionKey);

    if (!sessionId) {
        sessionId = generateSessionId();
        sessionStorage.setItem(sessionKey, sessionId);
    }

    return sessionId;
};

/**
 * Get available slots for a profile on a specific date
 */
export const getAvailableSlots = async (
    username: string,
    date: string,
    serviceIds: string[] = [],
    assignedUserId?: string
): Promise<BookingAvailabilityResponse> => {
    const params = new URLSearchParams({ date });

    if (serviceIds.length > 0) {
        serviceIds.forEach((id) => params.append('service_ids[]', id));
    }
    if (assignedUserId) {
        params.set('assigned_user_id', assignedUserId);
    }

    const response = await apiClient.get<{ data: BookingAvailabilityResponse }>(
        `/profiles/${username}/availability?${params.toString()}`
    );

    return response.data.data;
};

/**
 * Lock a slot for 15 minutes during checkout
 */
export const lockSlot = async (
    profileId: string,
    date: string,
    time: string,
    assignedUserId?: string
): Promise<{ success: boolean; message: string }> => {
    const sessionId = getBookingSessionId();

    const response = await apiClient.post<{ success: boolean; message: string }>(
        '/bookings/lock',
        {
            profile_id: profileId,
            date,
            time,
            session_id: sessionId,
            assigned_user_id: assignedUserId,
        }
    );

    return response.data;
};

/**
 * Create a pending booking
 */
export const createBooking = async (
    data: CreateBookingRequest
): Promise<{
    booking_id: string;
    status: string;
    expires_at?: string;
    verification_method?: 'email' | 'phone' | 'manual';
    client_verification_enabled?: boolean;
}> => {
    const sessionId = getBookingSessionId();

    const response = await apiClient.post<{
        success: boolean;
        message: string;
        data: {
            booking_id: string;
            status: string;
            expires_at?: string;
            verification_method?: 'email' | 'phone' | 'manual';
            client_verification_enabled?: boolean;
        };
    }>('/bookings', {
        ...data,
        session_id: sessionId,
    });

    return response.data.data;
};

export const getServiceMaterials = async (
    username: string,
    serviceIds: string[]
): Promise<
    Array<{
        id: string;
        name: string;
        description?: string | null;
        unit?: string | null;
        base_price: number;
        is_required: boolean;
        allows_variant_selection: boolean;
        default_quantity: number;
        included_in_price: boolean;
        service_ids: string[];
        variants: Array<{
            id: string;
            name: string;
            effective_price: number;
            attributes?: Record<string, unknown>;
        }>;
    }>
> => {
    const params = new URLSearchParams();
    serviceIds.forEach((id) => params.append('service_ids[]', id));
    const response = await apiClient.get<{ success: boolean; data: unknown[] }>(
        `/profiles/${username}/service-materials?${params.toString()}`
    );
    return response.data.data as ReturnType<typeof getServiceMaterials> extends Promise<infer R> ? R : never;
};

export const getStaffBookingLocations = async (
    userId: string
): Promise<
    Array<{
        id: string;
        business_name: string | null;
        slug: string;
        location?: string | null;
        is_primary?: boolean;
    }>
> => {
    const response = await apiClient.get<{ success: boolean; data: unknown[] }>(
        `/staff/${userId}/booking-locations`
    );
    return response.data.data as Array<{
        id: string;
        business_name: string | null;
        slug: string;
        location?: string | null;
        is_primary?: boolean;
    }>;
};

/**
 * Verify a booking via token (from email link)
 */
export const verifyBooking = async (
    token: string
): Promise<{
    booking_id: string;
    status: string;
    booking_date: string;
    booking_time: string;
    services: Array<{ name: string; duration: number }>;
}> => {
    const response = await apiClient.get<{
        success: boolean;
        message: string;
        data: {
            booking_id: string;
            status: string;
            booking_date: string;
            booking_time: string;
            services: Array<{ name: string; duration: number }>;
        };
    }>(`/bookings/verify/${token}`);

    return response.data.data;
};

/**
 * Get booking details by token
 */
export const getBookingByToken = async (
    token: string
): Promise<{
    id: string;
    status: string;
    customer_name: string;
    booking_date: string;
    booking_time: string;
    duration_minutes: number;
    total_price: number | null;
    services: Array<{ name: string; duration: number; price: number }>;
    profile: { business_name: string };
    verified_at: string | null;
    confirmed_at: string | null;
}> => {
    const response = await apiClient.get<{
        success: boolean;
        data: {
            id: string;
            status: string;
            customer_name: string;
            booking_date: string;
            booking_time: string;
            duration_minutes: number;
            total_price: number | null;
            services: Array<{ name: string; duration: number; price: number }>;
            profile: { business_name: string };
            verified_at: string | null;
            confirmed_at: string | null;
        };
    }>(`/bookings/${token}`);

    return response.data.data;
};

/**
 * Poll booking status until confirmed or max polls reached
 */
export const pollBookingStatus = async (
    token: string,
    maxPolls: number = 60,  // 5 minutes max (60 * 5s)
    intervalMs: number = 5000
): Promise<{ status: string; booking: Booking }> => {
    for (let i = 0; i < maxPolls; i++) {
        const booking = await getBookingByToken(token);
        if (booking.status === 'confirmed' || booking.status !== 'pending_verification') {
            return { status: booking.status, booking: booking as unknown as Booking };
        }
        await new Promise(resolve => setTimeout(resolve, intervalMs));
    }
    throw new Error('Polling timed out');
};

/**
 * Cancel a booking
 */
export const cancelBooking = async (
    token: string,
    reason?: string
): Promise<{ booking_id: string; status: string }> => {
    const response = await apiClient.post<{
        success: boolean;
        message: string;
        data: { booking_id: string; status: string };
    }>(`/bookings/${token}/cancel`, { reason });

    return response.data.data;
};

/**
 * Get provider's bookings (authenticated)
 */
export const getProviderBookings = async (params?: {
    status?: string;
    from?: string;
    to?: string;
    from_date?: string;
    to_date?: string;
    per_page?: number;
    page?: number;
    aggregate?: boolean;
}) => {
    const queryParams = new URLSearchParams();

    if (params?.status) queryParams.set('status', params.status);
    const from = params?.from ?? params?.from_date;
    const to = params?.to ?? params?.to_date;
    if (from) queryParams.set('from', from);
    if (to) queryParams.set('to', to);
    if (params?.per_page) queryParams.set('per_page', String(params.per_page));
    if (params?.page) queryParams.set('page', String(params.page));
    if (params?.aggregate) queryParams.set('aggregate', 'true');

    const response = await apiClient.get<{
        data: {
            data: Booking[];
            current_page: number;
            last_page: number;
            total: number;
        };
    }>(`/provider/bookings?${queryParams.toString()}`);

    return response.data.data;
};

/**
 * Create a manual booking (provider-initiated)
 */
export const createManualBooking = async (data: {
    customer_name: string;
    customer_email: string;
    customer_phone?: string;
    service_ids: string[];
    booking_date: string;
    booking_time: string;
    customer_notes?: string;
    pre_confirmed?: boolean;
    assigned_user_id?: string | null;
}): Promise<Booking> => {
    const response = await apiClient.post<{
        success: boolean;
        data: Booking;
        message: string;
    }>('/provider/bookings', data);

    return response.data.data;
};

/**
 * Get services for the authenticated provider
 */
export const getProviderServices = async (): Promise<{
    categories: ServiceCategory[];
    uncategorized: Service[];
}> => {
    const response = await apiClient.get<{
        data: {
            categories: ServiceCategory[];
            uncategorized: Service[];
        };
    }>('/user/services');

    return response.data.data;
};

/**
 * Get provider booking by ID (authenticated)
 */
export const getProviderBooking = async (id: string): Promise<Booking> => {
    const response = await apiClient.get<{
        success: boolean;
        data: Booking;
    }>(`/provider/bookings/${id}`);

    return response.data.data;
};

/**
 * Confirm a pending booking (authenticated)
 */
export const confirmBooking = async (
    id: string
): Promise<Booking> => {
    const response = await apiClient.put<{
        success: boolean;
        data: Booking;
    }>(`/provider/bookings/${id}/confirm`);

    return response.data.data;
};

/**
 * Assign or reassign staff on a booking
 */
export const assignBookingStaff = async (
    bookingId: string,
    assignedUserId: string | null
): Promise<Booking> => {
    const response = await apiClient.put<{ success: boolean; data: Booking }>(
        `/provider/bookings/${bookingId}/assign`,
        { assigned_user_id: assignedUserId }
    );
    return response.data.data;
};

/**
 * Cancel a booking as provider (authenticated)
 */
export const cancelProviderBooking = async (
    id: string,
    reason?: string
): Promise<Booking> => {
    const response = await apiClient.put<{
        success: boolean;
        data: Booking;
    }>(`/provider/bookings/${id}/cancel`, { reason });

    return response.data.data;
};

/**
 * Get today's bookings for the dashboard calendar view (authenticated)
 */
export const getTodaysBookings = async (): Promise<Booking[]> => {
    const today = new Date().toISOString().split('T')[0];

    const response = await apiClient.get<{
        data: {
            data: Booking[];
            current_page: number;
            last_page: number;
            total: number;
        };
    }>(`/provider/bookings?from_date=${today}&to_date=${today}`);

    return response.data.data.data;
};

/**
 * Complete a booking (authenticated)
 */
export const completeBooking = async (id: string): Promise<Booking> => {
    const response = await apiClient.put<{
        success: boolean;
        data: Booking;
    }>(`/provider/bookings/${id}/complete`);

    return response.data.data;
};

/**
 * Reject a pending booking (authenticated)
 */
export const rejectBooking = async (id: string, reason?: string): Promise<Booking> => {
    const response = await apiClient.put<{
        success: boolean;
        data: Booking;
    }>(`/provider/bookings/${id}/reject`, { reason });

    return response.data.data;
};

/**
 * Mark booking as no-show (authenticated)
 */
export const markNoShow = async (id: string): Promise<Booking> => {
    const response = await apiClient.put<{
        success: boolean;
        data: Booking;
    }>(`/provider/bookings/${id}/no-show`);

    return response.data.data;
};

/**
 * Update internal notes on a booking (authenticated)
 */
export const updateInternalNotes = async (id: string, internalNotes: string): Promise<Booking> => {
    const response = await apiClient.put<{
        success: boolean;
        data: Booking;
    }>(`/provider/bookings/${id}/notes`, { internal_notes: internalNotes });

    return response.data.data;
};

/**
 * Verify booking via phone OTP
 */
export const verifyPhone = async (data: {
    booking_id?: string;
    token?: string;
    code: string;
}): Promise<{ booking_id: string; status: string }> => {
    const response = await apiClient.post<{
        success: boolean;
        message: string;
        data: { booking_id: string; status: string };
    }>('/bookings/verify-phone', data);

    return response.data.data;
};

/**
 * Upload a reference image for booking
 */
export const uploadBookingImage = async (
    file: File,
    context: {
        profileId: string;
        date: string;
        time: string;
        sessionId: string;
        assignedUserId?: string | null;
    }
): Promise<string> => {
    const formData = new FormData();
    formData.append('image', file);
    formData.append('profile_id', context.profileId);
    formData.append('date', context.date);
    formData.append('time', context.time);
    formData.append('session_id', context.sessionId);
    if (context.assignedUserId) {
        formData.append('assigned_user_id', context.assignedUserId);
    }

    const response = await apiClient.post<{
        success: boolean;
        data: { path: string };
    }>('/bookings/images', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });

    return response.data.data.path;
};

/**
 * Get bundled services for a public profile (includes linked locations)
 */
export const getBundledServices = async (username: string): Promise<Service[]> => {
    const response = await apiClient.get<{
        success: boolean;
        data: Service[];
    }>(`/profiles/${username}/bundled-services`);

    return response.data.data;
};

// ============ Location / Profile Management ============

export const getUserProfiles = async (): Promise<LocationProfile[]> => {
    const response = await apiClient.get<{
        success: boolean;
        data: LocationProfile[];
    }>('/user/profiles');

    return response.data.data;
};

export const createUserProfile = async (data: {
    business_name: string;
    location?: string;
    currency?: string;
    supported_currencies?: string[];
}): Promise<LocationProfile> => {
    const response = await apiClient.post<{
        success: boolean;
        data: LocationProfile;
    }>('/user/profiles', data);

    return response.data.data;
};

export const updateUserProfile = async (
    id: string,
    data: Partial<LocationProfile>
): Promise<LocationProfile> => {
    const response = await apiClient.patch<{
        success: boolean;
        data: LocationProfile;
    }>(`/user/profiles/${id}`, data);

    return response.data.data;
};

export const makeProfilePrimary = async (id: string): Promise<LocationProfile> => {
    const response = await apiClient.post<{
        success: boolean;
        data: LocationProfile;
    }>(`/user/profiles/${id}/make-primary`);

    return response.data.data;
};

export const getLinkedProfiles = async (profileId: string): Promise<LocationProfile[]> => {
    const response = await apiClient.get<{
        success: boolean;
        data: LocationProfile[];
    }>(`/user/profiles/${profileId}/linked`);

    return response.data.data;
};

export const linkProfile = async (
    profileId: string,
    profileBId: string
): Promise<void> => {
    await apiClient.post(`/user/profiles/${profileId}/link`, {
        profile_b_id: profileBId,
    });
};

export const unlinkProfile = async (
    profileId: string,
    linkedId: string
): Promise<void> => {
    await apiClient.delete(`/user/profiles/${profileId}/linked/${linkedId}`);
};

/**
 * Get booking settings (authenticated)
 */
export const getBookingSettings = async (): Promise<BookingSettings> => {
    const response = await apiClient.get<{
        success: boolean;
        data: BookingSettings;
    }>('/provider/booking-settings');

    return response.data.data;
};

/**
 * Update booking settings (authenticated)
 */
export const updateBookingSettings = async (
    settings: Partial<BookingSettings>
): Promise<BookingSettings> => {
    const response = await apiClient.put<{
        success: boolean;
        data: BookingSettings;
    }>('/provider/booking-settings', settings);

    return response.data.data;
};

/**
 * Get break times for profile
 */
export const getBreakTimes = async (): Promise<BreakTime[]> => {
    const response = await apiClient.get<{ success: boolean; data: BreakTime[] }>('/user/break-times');
    return response.data.data;
};

/**
 * Create break time
 */
export const createBreakTime = async (data: {
    type: 'recurring' | 'date_specific';
    day_of_week?: number;
    specific_date?: string;
    start_time: string;
    end_time: string;
    label?: string;
}): Promise<BreakTime> => {
    const response = await apiClient.post<{ success: boolean; data: BreakTime }>('/user/break-times', data);
    return response.data.data;
};

/**
 * Update break time
 */
export const updateBreakTime = async (id: string, data: Partial<BreakTime>): Promise<BreakTime> => {
    const response = await apiClient.put<{ success: boolean; data: BreakTime }>(`/user/break-times/${id}`, data);
    return response.data.data;
};

/**
 * Delete break time
 */
export const deleteBreakTime = async (id: string): Promise<void> => {
    await apiClient.delete(`/user/break-times/${id}`);
};

/**
 * Get peak pricing rules
 */
export const getPeakPricingRules = async (): Promise<PeakPricingRule[]> => {
    const response = await apiClient.get<{ success: boolean; data: PeakPricingRule[] }>('/user/peak-pricing-rules');
    return response.data.data;
};

/**
 * Create peak pricing rule
 */
export const createPeakPricingRule = async (data: {
    service_id?: string;
    day_of_week: number;
    start_time: string;
    end_time: string;
    multiplier: number;
    is_off_peak: boolean;
    label?: string;
}): Promise<PeakPricingRule> => {
    const response = await apiClient.post<{ success: boolean; data: PeakPricingRule }>('/user/peak-pricing-rules', data);
    return response.data.data;
};

/**
 * Update peak pricing rule
 */
export const updatePeakPricingRule = async (id: string, data: Partial<PeakPricingRule>): Promise<PeakPricingRule> => {
    const response = await apiClient.put<{ success: boolean; data: PeakPricingRule }>(`/user/peak-pricing-rules/${id}`, data);
    return response.data.data;
};

/**
 * Delete peak pricing rule
 */
export const deletePeakPricingRule = async (id: string): Promise<void> => {
    await apiClient.delete(`/user/peak-pricing-rules/${id}`);
};

/**
 * Get upcoming holiday notifications
 */
export const getHolidayNotifications = async (): Promise<HolidayNotification> => {
    const response = await apiClient.get<{ success: boolean; data: HolidayNotification }>(
        '/user/holidays/upcoming'
    );
    return response.data.data;
};

/**
 * Dismiss a single holiday notification
 */
export const dismissHoliday = async (holidayId: string, notes?: string): Promise<void> => {
    await apiClient.post(`/user/holidays/dismiss/${holidayId}`, { notes });
};

/**
 * Dismiss all holiday notifications
 */
export const dismissAllHolidays = async (): Promise<{ dismissed_count: number }> => {
    const response = await apiClient.post<{ success: boolean; data: { dismissed_count: number } }>(
        '/user/holidays/dismiss-all'
    );
    return response.data.data;
};

/**
 * Get dismissed holidays list
 */
export const getDismissedHolidays = async (): Promise<DismissedHoliday[]> => {
    const response = await apiClient.get<{ success: boolean; data: DismissedHoliday[] }>(
        '/user/holidays/dismissed'
    );
    return response.data.data;
};

export { getBookingSessionId };

export interface SlotLock {
    id: string;
    date: string;
    time: string;
    profile_id: string;
    progress: {
        elapsed_seconds: number;
        remaining_seconds: number;
        percentage: number;
        status: 'green' | 'yellow' | 'red';
    };
}

/**
 * Get active slot locks for the authenticated provider
 */
export const getProviderSlotLocks = async (): Promise<SlotLock[]> => {
    const response = await apiClient.get<{
        success: boolean;
        data: SlotLock[];
    }>('/provider/slot-locks');
    return response.data.data;
};

/**
 * Register for slot availability notification
 */
export const registerSlotNotification = async (data: {
    profile_id: string;
    service_ids?: string[];
    date: string;
    time: string;
    email: string;
}): Promise<{ position: number }> => {
    const response = await apiClient.post<{
        success: boolean;
        data: { position: number };
    }>('/slot-notifications', data);
    return response.data.data;
};

// ============ Customer Management Functions ============

/**
 * Get customers for the authenticated provider
 */
export const getCustomers = async (params?: { search?: string; banned?: boolean }): Promise<{
    data: Customer[];
    current_page: number;
    last_page: number;
    total: number;
}> => {
    const queryParams = new URLSearchParams();

    if (params?.search) queryParams.set('search', params.search);
    if (params?.banned !== undefined) queryParams.set('banned', String(params.banned));

    const response = await apiClient.get<{
        success: boolean;
        data: {
            data: Customer[];
            current_page: number;
            last_page: number;
            total: number;
        };
    }>(`/provider/customers?${queryParams.toString()}`);

    return response.data.data;
};

/**
 * Get a single customer by ID
 */
export const getCustomer = async (id: string): Promise<Customer> => {
    const response = await apiClient.get<{
        success: boolean;
        data: Customer;
    }>(`/provider/customers/${id}`);

    return response.data.data;
};

/**
 * Create a new customer
 */
export const createCustomer = async (data: {
    name: string;
    email: string;
    phone?: string;
}): Promise<Customer> => {
    const response = await apiClient.post<{
        success: boolean;
        data: Customer;
    }>('/provider/customers', data);

    return response.data.data;
};

/**
 * Update an existing customer
 */
export const updateCustomer = async (id: string, data: Partial<Customer>): Promise<Customer> => {
    const response = await apiClient.put<{
        success: boolean;
        data: Customer;
    }>(`/provider/customers/${id}`, data);

    return response.data.data;
};

/**
 * Delete a customer
 */
export const deleteCustomer = async (id: string): Promise<void> => {
    await apiClient.delete(`/provider/customers/${id}`);
};

/**
 * Ban a customer with a reason
 */
export const banCustomer = async (id: string, reason: string): Promise<Customer> => {
    const response = await apiClient.post<{
        success: boolean;
        data: Customer;
    }>(`/provider/customers/${id}/ban`, { reason });

    return response.data.data;
};

/**
 * Unban a customer
 */
export const unbanCustomer = async (id: string): Promise<Customer> => {
    const response = await apiClient.post<{
        success: boolean;
        data: Customer;
    }>(`/provider/customers/${id}/unban`);

    return response.data.data;
};

/**
 * Get notes for a customer
 */
export const getCustomerNotes = async (customerId: string): Promise<CustomerNote[]> => {
    const response = await apiClient.get<{
        success: boolean;
        data: CustomerNote[];
    }>(`/provider/customers/${customerId}/notes`);

    return response.data.data;
};

/**
 * Add a note to a customer
 */
export const addCustomerNote = async (
    customerId: string,
    note: string,
    isPrivate: boolean
): Promise<CustomerNote> => {
    const response = await apiClient.post<{
        success: boolean;
        data: CustomerNote;
    }>(`/provider/customers/${customerId}/notes`, { note, is_private: isPrivate });

    return response.data.data;
};

/**
 * Delete a customer note
 */
export const deleteCustomerNote = async (customerId: string, noteId: string): Promise<void> => {
    await apiClient.delete(`/provider/customers/${customerId}/notes/${noteId}`);
};

/**
 * Get activities for a customer
 */
export const getCustomerActivities = async (customerId: string): Promise<CustomerActivity[]> => {
    const response = await apiClient.get<{
        success: boolean;
        data: CustomerActivity[];
    }>(`/provider/customers/${customerId}/activities`);

    return response.data.data;
};

// ============ Analytics Functions ============

/**
 * Get booking analytics for the provider
 */
export const getProviderAnalytics = async (
    startDate?: string,
    endDate?: string
): Promise<BookingAnalytics> => {
    const queryParams = new URLSearchParams();

    if (startDate) queryParams.set('start_date', startDate);
    if (endDate) queryParams.set('end_date', endDate);

    const response = await apiClient.get<{
        success: boolean;
        data: BookingAnalytics;
    }>(`/provider/analytics?${queryParams.toString()}`);

    return response.data.data;
};

/**
 * Get completion time statistics
 */
export const getCompletionStats = async (): Promise<BookingAnalytics['completion_time_stats']> => {
    const response = await apiClient.get<{
        success: boolean;
        data: BookingAnalytics['completion_time_stats'];
    }>('/provider/analytics/completion-stats');

    return response.data.data;
};

/**
 * Start booking timer (start time tracking)
 */
export const startBookingTimer = async (bookingId: string): Promise<Booking> => {
    const response = await apiClient.post<{
        success: boolean;
        data: Booking;
    }>(`/provider/bookings/${bookingId}/start`);

    return response.data.data;
};

/**
 * Complete booking timer (stop time tracking and complete booking)
 */
export const completeBookingTimer = async (bookingId: string): Promise<Booking> => {
    const response = await apiClient.post<{
        success: boolean;
        data: Booking;
    }>(`/provider/bookings/${bookingId}/complete`);

    return response.data.data;
};

/**
 * Get portfolio analytics for the provider
 */
export const getPortfolioAnalytics = async (
    startDate?: string,
    endDate?: string
): Promise<PortfolioAnalytics> => {
    const queryParams = new URLSearchParams();

    if (startDate) queryParams.set('start_date', startDate);
    if (endDate) queryParams.set('end_date', endDate);

    const response = await apiClient.get<{
        success: boolean;
        data: PortfolioAnalytics;
    }>(`/provider/analytics/portfolio?${queryParams.toString()}`);

    return response.data.data;
};

/**
 * Get page analytics for the provider
 */
export const getPageAnalytics = async (
    startDate?: string,
    endDate?: string
): Promise<PageAnalytics> => {
    const queryParams = new URLSearchParams();

    if (startDate) queryParams.set('start_date', startDate);
    if (endDate) queryParams.set('end_date', endDate);

    const response = await apiClient.get<{
        success: boolean;
        data: PageAnalytics;
    }>(`/provider/analytics/pages?${queryParams.toString()}`);

    return response.data.data;
};

/**
 * Get funnel analytics for the provider
 */
export const getFunnelAnalytics = async (
    startDate?: string,
    endDate?: string
): Promise<FunnelAnalytics> => {
    const queryParams = new URLSearchParams();

    if (startDate) queryParams.set('start_date', startDate);
    if (endDate) queryParams.set('end_date', endDate);

    const response = await apiClient.get<{
        success: boolean;
        data: FunnelAnalytics;
    }>(`/provider/analytics/funnel?${queryParams.toString()}`);

    return response.data.data;
};

/**
 * Get funnel stats for the provider
 */
export const getFunnelStats = async (
    startDate?: string,
    endDate?: string
): Promise<FunnelStats> => {
    const queryParams = new URLSearchParams();

    if (startDate) queryParams.set('start_date', startDate);
    if (endDate) queryParams.set('end_date', endDate);

    const response = await apiClient.get<{
        success: boolean;
        data: FunnelStats;
    }>(`/provider/analytics/funnel-stats?${queryParams.toString()}`);

    return response.data.data;
};

/**
 * Get time spent statistics for the provider
 */
export const getTimeSpentStats = async (
    startDate?: string,
    endDate?: string
): Promise<TimeSpentStats> => {
    const queryParams = new URLSearchParams();

    if (startDate) queryParams.set('start_date', startDate);
    if (endDate) queryParams.set('end_date', endDate);

    const response = await apiClient.get<{
        success: boolean;
        data: TimeSpentStats;
    }>(`/provider/analytics/time-spent?${queryParams.toString()}`);

    return response.data.data;
};
